import {
  useAddKnowledgeBaseFileMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useGetKnowledgeBaseFilesQuery,
} from 'src/services';
import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { KNOWLEDGE_BASE_FILE_EXTENSIONS } from 'src/shared/constants';
import { ZendeskHelpCenterAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ZendeskHelpCenterAccordion/ZendeskHelpCenterAccordion';
import { ZendeskTicketsAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ZendeskTicketsAccordion/ZendeskTicketsAccordion';
import { FreshdeskTicketsAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/FreshdeskTicketsAccordion/FreshdeskTicketsAccordion';
import { ScrapeWebsiteAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ScrapeWebsiteAccordion/ScrapeWebsiteAccordion';
import { UploadFilesAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/UploadFilesAccordion/UploadFilesAccordion';
import { useLoader } from 'src/provider/LoaderProvider';
import { AddFAQAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddFAQAccordion/AddFAQAccordion';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import type { KnowledgeBase, KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';
import type { KBFile } from 'src/utils/fileUtils';

const sortByName = (a: { name: string }, b: { name: string }) => {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
};

export const CreateNewKBDataSources = ({
  item,
  onCollectorsChanged = () => {},
  onDisabledChange = () => {},
}: {
  // The knowledge base being created; only its id is used.
  item?: Pick<KnowledgeBase, 'id'>;
  onCollectorsChanged?: (collectors: KnowledgeBaseCollector[]) => void;
  // Reports whether no data source has been added yet (onboarding disables Next until one is).
  onDisabledChange?: (disabled: boolean) => void;
}) => {
  const inputFile = useRef(null);
  const [fileInputValue, setFileInputValue] = useState('');
  const loader = useLoader();
  const [websites, setWebsites] = useState<KnowledgeBaseCollector[]>([]);
  const [files, setFiles] = useState<KBFile[]>([]);
  const [faqs, setFaqs] = useState<KBFile[]>([]);
  const [zendeskHelpCenter, setZendeskHelpCenter] = useState<KnowledgeBaseCollector | null>(null);
  const [zendeskTickets, setZendeskTickets] = useState<KnowledgeBaseCollector | null>(null);
  const [freshdeskTickets, setFreshdeskTickets] = useState<KnowledgeBaseCollector | null>(null);
  const [refetchState, setRefetchState] = useState(false);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false);
  const { skipRefetchFiles } = useKBContext();

  const [addKnowledgeBaseFile] = useAddKnowledgeBaseFileMutation();
  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: item.id,
        include_data: true,
      },
      {
        skip: !item?.id,
        refetchOnMountOrArgChange: true,
      },
    );
  const {
    data: filesResponse,
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
  } = useGetKnowledgeBaseFilesQuery(
    {
      id: item.id,
      sync: false,
    },
    {
      skip: !item.id,
    },
  );

  useEffect(() => {
    const newFaqs: KBFile[] = [];
    const newFiles: KBFile[] = [];

    if (!skipRefetchFiles && filesResponse) {
      const filteredFiles = filesResponse.filter((file) => !file.collector_id);

      filteredFiles.forEach((file) => {
        if (file.name.endsWith('.faq')) {
          newFaqs.push(file);
        } else {
          newFiles.push(file);
        }
      });

      newFaqs.sort(sortByName);
      newFiles.sort(sortByName);
      setFaqs(newFaqs);
      setFiles(newFiles);
    }
  }, [filesResponse, skipRefetchFiles]);

  useEffect(() => {
    if (collectorsResponse) {
      const collectors: {
        zendeskTickets: KnowledgeBaseCollector | null;
        zendeskHelpCenter: KnowledgeBaseCollector | null;
        freshdeskTickets: KnowledgeBaseCollector | null;
        websites: KnowledgeBaseCollector[];
      } = {
        zendeskTickets: null,
        zendeskHelpCenter: null,
        freshdeskTickets: null,
        websites: [],
      };

      if (!skipRefetchFiles && collectorsResponse) {
        collectorsResponse.forEach((collector) => {
          switch (collector.type) {
            case 'web_scrapper':
              collectors.websites.push({ ...collector });
              break;
            case 'freshdesk_tickets':
              collectors.freshdeskTickets = collector;
              break;
            case 'zendesk_helpcenter':
              collectors.zendeskHelpCenter = collector;
              break;
            case 'zendesk_tickets':
              collectors.zendeskTickets = collector;
              break;
          }
        });

        setWebsites(collectors.websites);
        setFreshdeskTickets(collectors.freshdeskTickets);
        setZendeskTickets(collectors.zendeskTickets);
        setZendeskHelpCenter(collectors.zendeskHelpCenter);
        onCollectorsChanged(collectorsResponse);
      }
    }
  }, [skipRefetchFiles, collectorsResponse]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (files && faqs) {
      const allFiles = [...files, ...faqs];
      const deletingExist = allFiles.some((file) => file.state === FILE_STATE.DELETING);
      const uploadingExist = allFiles.some((file) => file.state === FILE_STATE.UPLOADING);

      if (!skipRefetchFiles && (deletingExist || uploadingExist)) {
        setIsButtonsDisabled(true);
        setRefetchState(true);
        timeout = setTimeout(async () => {
          await refetchFiles();
          setRefetchState(false);
        }, 3000);
      } else {
        setIsButtonsDisabled(false);
      }
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [files, faqs, refetchState, skipRefetchFiles]);

  useEffect(() => {
    const isEnabled =
      freshdeskTickets ||
      zendeskTickets ||
      zendeskHelpCenter ||
      files.length ||
      faqs.length ||
      websites.length;

    onDisabledChange(!isEnabled);
  }, [files, faqs, websites, freshdeskTickets, zendeskHelpCenter, zendeskTickets]);

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files.length) {
      return;
    }

    const newFiles = uploadFiles(event.target.files);
    setFiles([...files, ...newFiles].sort(sortByName));
  };

  const uploadFiles = (uploadFiles: FileList) => {
    const promises: Promise<unknown>[] = [];
    const newFiles: KBFile[] = [];

    loader.show();

    if (uploadFiles.length) {
      setIsButtonsDisabled(true);
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles.item(i);
        const request = new FormData();

        request.append('file', file, file.name);
        request.append('file_path', '/');

        const response = addKnowledgeBaseFile({
          id: item.id,
          body: request,
        });

        newFiles.push({
          name: file.name,
          path: '/',
          state: FILE_STATE.UPLOADING,
        });

        promises.push(response);
      }
    }

    Promise.allSettled(promises).then(() => {
      setIsButtonsDisabled(false);
      setFileInputValue('');
      !skipRefetchFiles && refetchFiles();
    });

    loader.hide();

    return newFiles;
  };

  const addFAQ = (newFaqs: KBFile[]) => {
    setFaqs([...faqs, ...newFaqs].sort(sortByName));
  };

  return (
    <div
      className={clsx(
        'w-full',
        (isLoadingCollectors || isLoadingFiles) && "'blur-sm animate-pulse pointer-events-none'",
      )}
    >
      <input
        type="file"
        ref={inputFile}
        className={'hidden'}
        onChange={onFileChange}
        multiple={true}
        accept={KNOWLEDGE_BASE_FILE_EXTENSIONS.filter((ext) => ext !== '.faq').join(', ')}
        value={fileInputValue}
      />
      <div className={'flex flex-col gap-[5px] py-1 h-full overflow-auto relative'}>
        <div className={clsx('w-full flex flex-row items-start gap-5 mt-2')}>
          <div>
            <CheckIcon className={'invisible'} />
          </div>
          <span className="text-xs leading-5 font-medium text-ebony_opacity_50 select-none">
            Data sources:
          </span>
        </div>
        <ScrapeWebsiteAccordion
          items={websites}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
        <UploadFilesAccordion
          items={files}
          upload={() => inputFile.current.click()}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
        <AddFAQAccordion
          items={faqs}
          add={addFAQ}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
        <ZendeskHelpCenterAccordion
          item={zendeskHelpCenter}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
        <ZendeskTicketsAccordion
          item={zendeskTickets}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
        <FreshdeskTicketsAccordion
          item={freshdeskTickets}
          isButtonsDisabled={isButtonsDisabled}
          knowledgeBase={item}
        />
      </div>
    </div>
  );
};
